import { videos, categories, authors, type VideoItem, type CategoryItem } from "@/mocks/videos";

export type SortKey = "hot" | "new" | "likes";
export type TimeRange = "all" | "week" | "month";

export function getAllVideos(): VideoItem[] {
  return videos;
}

export function getVideoById(id?: string): VideoItem | undefined {
  return videos.find((v) => v.id === id);
}

export function getCategoryById(id?: string): CategoryItem | undefined {
  return categories.find((c) => c.id === id);
}

export function getAuthorById(id?: string) {
  return authors.find((a) => a.id === id);
}

export function getVideosByCategory(categoryId: string): VideoItem[] {
  return videos.filter((v) => v.categoryId === categoryId);
}

export function getVideosByIds(ids: string[]): VideoItem[] {
  return ids
    .map((id) => videos.find((v) => v.id === id))
    .filter((v): v is VideoItem => Boolean(v));
}

export function searchVideos(keyword: string): VideoItem[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return videos.filter((v) => {
    const inTitle = v.title.toLowerCase().includes(q);
    const inAuthor = v.author.toLowerCase().includes(q);
    const inTags = v.tags.some((t) => t.toLowerCase().includes(q));
    const inCategory = v.categoryName.toLowerCase().includes(q);
    return inTitle || inAuthor || inTags || inCategory;
  });
}

export function getHotVideos(count = 8): VideoItem[] {
  return [...videos].sort((a, b) => b.views - a.views).slice(0, count);
}

export function getLatestVideos(count = 10): VideoItem[] {
  return [...videos]
    .sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime())
    .slice(0, count);
}

export function getFeaturedVideos(count = 5): VideoItem[] {
  return [...videos].sort((a, b) => b.likes - a.likes).slice(0, count);
}

export function sortVideos(list: VideoItem[], sort: SortKey): VideoItem[] {
  const copy = [...list];
  if (sort === "new") {
    return copy.sort(
      (a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
    );
  }
  if (sort === "likes") {
    return copy.sort((a, b) => b.likes - a.likes);
  }
  return copy.sort((a, b) => b.views - a.views);
}

export function filterByTime(list: VideoItem[], range: TimeRange): VideoItem[] {
  if (range === "all") return list;
  const now = new Date("2026-10-13").getTime();
  const days = range === "week" ? 7 : 30;
  return list.filter((v) => {
    const diff = (now - new Date(v.publishDate).getTime()) / (1000 * 60 * 60 * 24);
    return diff <= days;
  });
}

export function getTagPool(categoryId?: string): string[] {
  const source = categoryId ? getVideosByCategory(categoryId) : videos;
  const set = new Set<string>();
  source.forEach((v) => v.tags.forEach((t) => set.add(t)));
  return Array.from(set);
}

export function getRelatedVideos(video: VideoItem, count = 4): VideoItem[] {
  const sameCategory = videos.filter(
    (v) => v.categoryId === video.categoryId && v.id !== video.id
  );
  const others = videos.filter(
    (v) => v.categoryId !== video.categoryId && v.id !== video.id
  );
  return [...sameCategory, ...others]
    .sort((a, b) => b.views - a.views)
    .slice(0, count);
}